import { Autocomplete, Stack, TextField, Typography, Popper, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useTBContext } from "../Contexts/useTBStates";
import { IOSSwitch } from "../../shared/Components/iosSwitch";
import { StyledIconButton } from "../../shared/Components/StyledIconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import { utcToLocalDateString, formatDateForDisplay } from "@moment-golf/shared";
import { resolveRivalryConfig } from "@moment-golf/types";

interface EventItemDisplayProps {
    orient: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    label: string;
    keyName: string;
    labelField?: string;
    optionsKeyName?: string;
    /** When selecting from options, also set this field to the option's _id (e.g. courseID when keyName is courseName) */
    idKeyName?: string;
    freeSolo?: boolean;
    hideLabel?: boolean;
    flipped?: boolean;
    extraFactor?: number | 1;
    locked?: boolean;
    removable?: boolean;
    defaultValue?: any;
}


export const EventItemDisplay = ({ 
        orient, label, keyName, labelField, optionsKeyName, idKeyName, freeSolo, hideLabel, flipped, extraFactor, locked, removable, defaultValue
}: EventItemDisplayProps) => {

    const { viewEvent, setViewEvent,
        factor,
        appTheme, 
        editMode,
        forceUpdate,
        optionsLoaded,
    } = useTBContext();
    
    const optionsEntry = optionsLoaded?.find((o: unknown) => (o as { keyName?: string })?.keyName === optionsKeyName) as { options?: unknown[] } | undefined;
    const thisOptions = optionsEntry?.options ?? [];

    function formatEditValue() {
        if (!viewEvent) return defaultValue ?? null;

        let newValue: any;

        if (keyName.toLowerCase().includes("date") || keyName.endsWith("At")) {

            if (viewEvent[keyName]) {
                newValue = utcToLocalDateString(viewEvent[keyName]);
            }
            else {
                newValue = null;
            }

        }
        else {
            newValue = viewEvent[keyName];
        }

        if (newValue === undefined || newValue === null && defaultValue) {
            newValue = defaultValue;
        }

        return newValue;
    }

    function formatDisplayValue(value?: any) {
        let newValue = value ?? viewEvent?.[keyName];

        if (newValue === undefined || newValue === null && defaultValue) {
            newValue = defaultValue;
        }

        if (keyName.toLowerCase().includes("date") || keyName.endsWith("At")) {

            if (newValue) {
                newValue = formatDateForDisplay(newValue);
            }
            else {
                newValue = null;
            }
        }

        else if (typeof newValue === 'string' && !isNaN(Number(newValue))) {
            newValue = Number(newValue);
        }
        else {
            newValue = newValue;
        }

        return newValue;
    }

    function getDataType() {
        if (!viewEvent) {
            return (defaultValue === true || defaultValue === false) ? 'boolean' : 'string';
        }
        let value = viewEvent[keyName];

        if (value === undefined || value === null && defaultValue !== undefined) {
            value = defaultValue;
        }
        
        // First check if the key indicates a date
        if (keyName.toLowerCase().includes("date") || keyName.endsWith("At") || keyName === "earlyRegCutoff") {
            // Return 'date' even if value doesn't exist yet
            return 'date';
        }
        
        // If value doesn't exist, use defaultValue type so boolean fields (e.g. flightPricing) show as switch
        if (value === undefined || value === null) {
            if (defaultValue === true || defaultValue === false) return 'boolean';
            return 'string';
        }
        
        // Then check other types
        if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(String(value).trim()))) {
            return 'number';
        } else if (typeof value === 'boolean') {
            return 'boolean';
        }
        
        return 'string';
    }

    const [blockValue, setBlockValue] = useState<any>(formatEditValue());
    const [displayValue, setDisplayValue] = useState<any>(formatDisplayValue());
    const [dataType, setDataType] = useState<string>(getDataType());

    useEffect(() => {
        setBlockValue(formatEditValue());
        setDisplayValue(formatDisplayValue());
    }, [forceUpdate, viewEvent?.[keyName]]);

    const isRow = orient === 'row' || orient === 'row-reverse';
    // Keep view/edit row height aligned so Event Details cards don't expand on Edit
    const rowMinHeight = 32 * factor * (extraFactor || 1);

    return (
        <>
            {editMode && !locked ? (
                <Stack
                    direction={orient}
                    spacing={1}
                    sx={{
                        width: '100%',
                        maxWidth: '100%',
                        minWidth: 0,
                        minHeight: isRow ? rowMinHeight : undefined,
                        boxSizing: 'border-box',
                        justifyContent: 'space-between',
                        alignItems: isRow ? 'center' : 'flex-start',
                    }}
                >
                    {!hideLabel && 
                        <Typography 
                            flex={2} 
                            width={orient === 'row' ? 'auto' : '100%'} 
                            textAlign="left"
                            sx={{ 
                                fontSize: 14*factor*(extraFactor || 1), 
                                fontWeight: 500,
                                lineHeight: 1.4,
                                minHeight: isRow ? rowMinHeight : undefined,
                                display: 'flex',
                                alignItems: 'center',
                                minWidth: 0,
                                flexShrink: 1,
                            }}>
                            {label}:
                            </Typography>}
                    {thisOptions && thisOptions.length > 0 && labelField && (
                           
                                <Autocomplete
                                fullWidth
                                freeSolo={freeSolo || false}
                                disableClearable={!removable}
                                options={thisOptions}
                                getOptionLabel={(option: unknown) => String((option as Record<string, unknown>)[labelField ?? ''] ?? '')}
                                value={thisOptions.find((o: unknown) => (o as Record<string, unknown>)[labelField ?? ''] === displayValue) ?? null}
                                onChange={(e, newValue: unknown) => {
                                    // Handle null value when clear button is clicked
                                    if (newValue === null) {
                                        // Set appropriate default based on data type
                                        const defaultVal = dataType === 'number' ? 0 : '';
                                        setBlockValue(defaultVal);
                                        setDisplayValue(defaultVal);
                                        setViewEvent((prevState) => {
                                            if (!prevState) return null;
                                            const update: Record<string, unknown> = { ...prevState, [keyName]: defaultVal };
                                            if (idKeyName) update[idKeyName] = '';
                                            return update as typeof prevState;
                                        });
                                    } else {
                                        const opt = newValue as Record<string, unknown>;
                                        const val = opt[labelField ?? ''] ?? '';
                                        setBlockValue(val);
                                        setDisplayValue(val);
                                        setViewEvent((prevState) => {
                                            if (!prevState) return null;
                                            const update: Record<string, unknown> = { ...prevState, [keyName]: val };
                                            if (idKeyName && opt._id != null) update[idKeyName] = opt._id;
                                            return update as typeof prevState;
                                        });
                                    }
                                }}
                                sx={{
                                    flex: 3,
                                    minWidth: 0,
                                    maxWidth: '100%',
                                    padding: 0,
                                    '& .MuiAutocomplete-inputRoot': {
                                        padding: 0,
                                        minHeight: isRow ? rowMinHeight : undefined,
                                    }
                                }}
                                renderInput={(params) => <TextField 
                                    {...params} 
                                    variant="outlined" 
                                    sx={{
                                        padding: 0,
                                        "& .MuiInputBase-input": {
                                            fontSize: 14*factor*(extraFactor || 1),
                                            height: isRow ? rowMinHeight - 2 : 14*factor*(extraFactor || 1),
                                            padding: isRow ? '0px 8px' : 1,
                                            boxSizing: 'border-box',
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            padding: '0px 0px',
                                            minHeight: isRow ? rowMinHeight : undefined,
                                            height: isRow ? rowMinHeight : undefined,
                                        },
                                        '& input': {textAlign: flipped ? 'right' : 'left', lineHeight: 'normal'},
                                        flex: 3,
                                        width: '100%',
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}
                                    inputProps={{
                                        ...params.inputProps,
                                        inputMode: "text",
                                        autoComplete: "off",
                                        sx: {
                                            fontSize: 14 * (factor ? factor : 1) ,
                                            /* '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: appTheme.palette.primary.main,
                                            }, */
                                        },
                                    }}
                                    InputLabelProps={{
                                        sx: { fontSize: 16 * (factor ? factor : 1) * (extraFactor || 1), lineHeight: 'normal', padding: '0px 10px', backgroundColor: 'whitesmoke', marginTop: '0px' },
                                        shrink: true
                                    }}
                                />}
                                PopperComponent={(props) => (
                                    <Popper {...props} autoFocus sx={{ 
                                        zIndex: 1300, // Ensure it appears above other elements
                                        '& .MuiAutocomplete-listbox': {
                                            maxHeight: 200,
                                            overflowY: 'auto',
                                            bgcolor: 'whitesmoke',
                                        },
                                        '& .MuiAutocomplete-option': {
                                            padding: '5px 10px',
                                            '&:hover': {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3
                                            },
                                            fontSize: 14 * (factor ? factor : 1),
                                        },
                                        '& .Mui-focusVisible' : {
                                                backgroundColor: `${appTheme.palette.primary.extraLight} !important`,
                                                borderRadius: 3,
                                        },
                                        
                                    }} />
                                    
                            )}
                        />
                        
                    )}
                    {(!thisOptions || thisOptions.length === 0) && dataType !== 'boolean' && (
                            <TextField
                                value={dataType === 'date' ? blockValue : displayValue}
                                type={dataType}
                                onChange={(e) => {
                                    const newValue = e.target.value;
                                    const formattedValue = dataType === 'number' ? (newValue === '' ? '' : Number(newValue)) : newValue;
                                    setBlockValue(formattedValue);
                                    setDisplayValue(formattedValue);
                                    setViewEvent((prevState: any) => ({...prevState, [keyName]: formattedValue}));
                                }}
                                sx={{
                                    padding: 0,
                                    "& .MuiInputBase-input": {
                                        fontSize: 14*factor*(extraFactor || 1),
                                        height: isRow ? rowMinHeight - 2 : 14*factor*(extraFactor || 1),
                                        padding: isRow ? '0px 8px' : 1,
                                        boxSizing: 'border-box',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        padding: '0px 0px',
                                        minHeight: isRow ? rowMinHeight : undefined,
                                        height: isRow ? rowMinHeight : undefined,
                                    },
                                    '& input': {textAlign: flipped ? 'right' : 'left', lineHeight: 'normal'},
                                    flex: 3,
                                    width: '100%',
                                    minWidth: 0,
                                    maxWidth: '100%',
                                }}
                                InputProps={{
                                    sx: {
                                        fontSize: 14*factor*(extraFactor || 1),
                                    },
                                }}
                            />
                        )}
                    {dataType === 'boolean' && (
                        <Stack direction='row' flex={3} spacing={1} alignItems='center' sx={{ minWidth: 0, minHeight: isRow ? rowMinHeight : undefined }}>
                            <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography>
                            <IOSSwitch
                                checked={displayValue}
                                onChange={(e) => {
                                    const checked = e.target.checked;
                                    setBlockValue(checked);
                                    setDisplayValue(checked);
                                    setViewEvent((prevState: any) => {
                                        const next = { ...prevState, [keyName]: checked };
                                        if (keyName === 'useRivalry') {
                                            next.rivalry = checked
                                                ? resolveRivalryConfig({ useRivalry: true, rivalry: prevState.rivalry })
                                                : null;
                                        }
                                        return next;
                                    });
                                }}
                            />
                            <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                        </Stack>
                    )}
                    {/* {removable && (
                    <StyledIconButton
                            icon={<DeleteIcon />}
                            onClick={() => {
                                setDisplayValue("<removed>");
                                setViewEvent((prevState: any) => ({...prevState, [keyName]: "<removed>"}));
                            }}
                            appTheme={appTheme}
                            label=""
                            factor={factor}
                            color="error"
                            width={'max-content'}
                        />)} */}
                    </Stack>
            ) : 
            (
            <Stack
                direction={orient}
                spacing={orient === 'row' ? 1 : 0.5}
                sx={{
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    minHeight: isRow ? rowMinHeight : undefined,
                    boxSizing: 'border-box',
                    justifyContent: 'flex-start',
                    alignItems: isRow ? 'center' : 'flex-start',
                }}
            >
                {!hideLabel && <Typography 
                    flex={2} 
                    width={orient === 'row' ? 'auto' : '100%'} 
                    textAlign="left"
                    padding={0}
                    sx={{ 
                        fontWeight: 500, 
                        fontSize: 14*factor*(extraFactor || 1),
                        lineHeight: 1.4,
                        minHeight: isRow ? rowMinHeight : 24*factor*(extraFactor || 1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        minWidth: 0,
                        flexShrink: 1,
                    }}>
                    {label}:
                </Typography>}
                {dataType !== 'boolean' && (
                    <Typography 
                        flex={3} 
                        width={orient === 'row' ? 'auto' : '100%'} 
                        textAlign={flipped ? 'right' : 'left'} 
                        padding={0}
                        color={displayValue ? 'inherit' : 'text.disabled'}
                        sx={{ 
                            fontSize: 14*factor*(extraFactor || 1),
                            lineHeight: 1.4,
                            minHeight: isRow ? rowMinHeight : 24*factor*(extraFactor || 1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: flipped ? 'flex-end' : 'flex-start',
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            wordBreak: 'break-word',
                        }}>
                        {displayValue ? displayValue : label}
                    </Typography>

                )}
                {dataType === 'boolean' && (
                    <Stack direction='row' flex={3} spacing={1} alignItems='center' justifyContent='flex-start' sx={{ minWidth: 0, minHeight: isRow ? rowMinHeight : undefined }}>
                        <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>No</Typography>
                        <IOSSwitch
                            checked={blockValue}
                            disabled
                            onChange={(e) => {
                                setViewEvent((prevState: any) => ({...prevState, [keyName]: e.target.checked}));
                            }}
                        />
                        <Typography sx={{ fontSize: 14*factor*(extraFactor || 1), fontWeight: 500 }}>Yes</Typography>
                    </Stack>
                )}
            </Stack>
            )}
        </>
    )
}
